import { Badge, Button, Grid, Group, Modal, Paper, Stack, Switch, Text, TextInput, Textarea, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ProfileKeyFacts } from "../../components/profile/ProfileKeyFacts";
import { ProfileShell } from "../../components/profile/ProfileShell";
import { ProfileTimeline } from "../../components/profile/ProfileTimeline";
import { LoadingOverlay, ConfirmDialog } from "../../components/ui";
import { adminApi, profilesApi } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { HR_KYC_COPY } from "../../lib/companyKyc";
import { ACCESS_GRANT_COPY } from "../../lib/confirmDialog";
import { kycStatusLabel } from "../../lib/kyc";
import type { KycStatus } from "../../types/api";

export function UserProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const queryClient = useQueryClient();
  const [action, setAction] = useState<"deactivate" | "reactivate" | null>(null);
  const [hrRejectOpen, setHrRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const profileQuery = useQuery({
    queryKey: ["user-profile", userId],
    queryFn: () => profilesApi.getUserProfile(userId!),
    enabled: !!userId,
  });
  const timelineQuery = useQuery({
    queryKey: ["user-profile-timeline", userId],
    queryFn: () => profilesApi.getUserTimeline(userId!, { page_size: 100 }),
    enabled: !!userId,
  });

  const accountMutation = useMutation({
    mutationFn: ({ action, userId, reason }: { action: "deactivate" | "reactivate"; userId: string; reason: string }) =>
      action === "deactivate" ? adminApi.deactivateUser(userId, reason) : adminApi.reactivateUser(userId, reason),
    onSuccess: (_, variables) => {
      notifications.show({ color: "green", message: `Account ${variables.action === "deactivate" ? "deactivated" : "reactivated"}.` });
      setAction(null);
      setReason("");
      setConfirmation("");
      void queryClient.invalidateQueries({ queryKey: ["user-profile", userId] });
      void queryClient.invalidateQueries({ queryKey: ["user-profile-timeline", userId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-people"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-recruiters"] });
    },
    onError: (error) => notifications.show({ color: "red", message: error instanceof Error ? error.message : "Could not change account access" }),
  });

  const hrKycMutation = useMutation({
    mutationFn: (payload: { kyc_status?: KycStatus; kyc_bypass?: boolean; notes?: string }) => {
      const id =
        typeof profileQuery.data?.role_details?.company_user_id === "string"
          ? profileQuery.data.role_details.company_user_id
          : null;
      if (!id) throw new Error("Missing company user");
      return adminApi.setCompanyUserKyc(id, payload);
    },
    onSuccess: () => {
      notifications.show({ color: "green", message: "HR KYC updated." });
      setHrRejectOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["user-profile", userId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-people"] });
    },
    onError: (error) =>
      notifications.show({
        color: "red",
        message: error instanceof Error ? error.message : "Could not update HR KYC",
      }),
  });

  if (profileQuery.isLoading || timelineQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-280)" />;
  }
  if (!profileQuery.data) {
    return <Text c="dimmed">User profile not found.</Text>;
  }
  const profile = profileQuery.data;
  const membershipId = typeof profile.role_details?.company_user_id === "string" ? profile.role_details.company_user_id : null;
  const hrKycStatus = typeof profile.role_details?.kyc_status === "string" ? profile.role_details.kyc_status : null;
  const hrBypass = Boolean(profile.role_details?.kyc_bypass);
  const isDeactivated = profile.status === "suspended";
  const closeModal = () => {
    setAction(null);
    setReason("");
    setConfirmation("");
  };
  const backTo =
    profile.role === "admin"
      ? "/admin/people/admins"
      : profile.role === "affiliate"
        ? "/admin/affiliates"
        : profile.role === "recruiter"
          ? "/admin/recruiters"
          : "/admin/people/hr";
  return (
    <Stack gap="var(--axio-unit-32)">
      <Button component={Link} to={backTo} variant="subtle" w="fit-content">
        ← Back
      </Button>
      <ProfileShell
        name={profile.full_name}
        subtitle={`${profile.email} · joined ${formatDate(profile.created_at)}`}
        role={profile.role}
        status={profile.status}
        actions={
          <Button
            color={isDeactivated ? "teal" : "red"}
            variant={isDeactivated ? "light" : "filled"}
            onClick={() => setAction(isDeactivated ? "reactivate" : "deactivate")}
          >
            {isDeactivated ? "Reactivate account" : "Deactivate account"}
          </Button>
        }
        stats={
          <ProfileKeyFacts
            facts={[
              { label: "First login", value: profile.first_login_at ? formatDate(profile.first_login_at) : "—" },
              { label: "Access granted", value: profile.access_granted_at ? formatDate(profile.access_granted_at) : "—" },
              { label: "Last login", value: profile.last_login_at ? formatDate(profile.last_login_at) : "—" },
              { label: "Auth provider", value: profile.auth_provider },
            ]}
          />
        }
        timeline={<ProfileTimeline entries={timelineQuery.data?.items ?? []} />}
        beforeTabs={
          profile.role === "company_user" && membershipId ? (
            <Paper withBorder p="md">
              <Stack gap="md">
                <Group justify="space-between" align="flex-start" wrap="wrap">
                  <div>
                    <Title order={5}>{HR_KYC_COPY.pageTitle}</Title>
                    <Text size="sm" c="dimmed">
                      Status: {kycStatusLabel(hrKycStatus ?? undefined)}
                    </Text>
                  </div>
                  <Badge color={hrBypass ? "green" : "gray"} variant="light">
                    {hrBypass ? HR_KYC_COPY.bypassTitle : HR_KYC_COPY.bypassOff}
                  </Badge>
                </Group>
                <Text size="sm">
                  Designation: {typeof profile.role_details?.designation === "string" ? profile.role_details.designation : "—"}
                </Text>
                <Switch
                  label={HR_KYC_COPY.bypassToggle}
                  description={HR_KYC_COPY.bypassHint}
                  checked={hrBypass}
                  onChange={(e) => hrKycMutation.mutate({ kyc_bypass: e.currentTarget.checked })}
                />
                <Group>
                  <Button
                    size="xs"
                    color="green"
                    variant="light"
                    loading={hrKycMutation.isPending}
                    onClick={() => hrKycMutation.mutate({ kyc_status: "verified" })}
                  >
                    Verify
                  </Button>
                  <Button
                    size="xs"
                    color="red"
                    variant="light"
                    onClick={() => setHrRejectOpen(true)}
                  >
                    {ACCESS_GRANT_COPY.rejectCta}
                  </Button>
                </Group>
              </Stack>
            </Paper>
          ) : undefined
        }
        details={
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Paper withBorder p="md">
                <Title order={5} mb="sm">
                  Identity & Access
                </Title>
                <Stack gap="var(--axio-unit-6)">
                  <Text size="sm">Email: {profile.email}</Text>
                  <Text size="sm">Phone: {profile.phone ?? "—"}</Text>
                  <Text size="sm">Auth provider: {profile.auth_provider}</Text>
                  <Text size="sm">
                    Email verified: {profile.email_verified_at ? formatDate(profile.email_verified_at) : "No"}
                  </Text>
                </Stack>
              </Paper>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Paper withBorder p="md">
                <Title order={5} mb="sm">
                  Role details
                </Title>
                {Object.keys(profile.role_details ?? {}).length === 0 ? (
                  <Text size="sm" c="dimmed">
                    No additional role details.
                  </Text>
                ) : (
                  <Stack gap="var(--axio-unit-6)">
                    {Object.entries(profile.role_details).map(([k, v]) => (
                      <Text key={k} size="sm">
                        <Badge size="xs" mr="var(--axio-unit-6)" variant="light">
                          {k}
                        </Badge>
                        {String(v)}
                      </Text>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Grid.Col>
          </Grid>
        }
      />
      <ConfirmDialog
        opened={hrRejectOpen}
        onClose={() => setHrRejectOpen(false)}
        title={ACCESS_GRANT_COPY.rejectHrKycTitle}
        description={ACCESS_GRANT_COPY.rejectHrKycBody}
        confirmLabel={ACCESS_GRANT_COPY.rejectHrKycCta}
        loading={hrKycMutation.isPending}
        onConfirm={() =>
          hrKycMutation.mutate({ kyc_status: "rejected", notes: HR_KYC_COPY.rejectDefaultNotes })
        }
      />
      <Modal
        opened={action !== null}
        onClose={closeModal}
        title={action === "deactivate" ? "Deactivate this account?" : "Reactivate this account?"}
        centered
      >
        <Stack>
          <Text size="sm">
            {action === "deactivate"
              ? "This immediately blocks sign-in and releases any active recruiter call claims. Historical submissions and payouts are retained."
              : "Access will be restored, but calling seats and public intake links will remain disabled until you explicitly re-enable them."}
          </Text>
          <Textarea label="Reason" required minRows={3} value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
          <TextInput
            label={`Type ${profile.email} to confirm`}
            value={confirmation}
            onChange={(e) => setConfirmation(e.currentTarget.value)}
          />
          <Button
            color={action === "deactivate" ? "red" : "teal"}
            loading={accountMutation.isPending}
            disabled={reason.trim().length < 3 || confirmation.trim().toLowerCase() !== profile.email.toLowerCase()}
            onClick={() => userId && action && accountMutation.mutate({ action, userId, reason: reason.trim() })}
          >
            {action === "deactivate" ? "Deactivate permanently until reactivated" : "Reactivate account"}
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
